import { useLayoutEffect, useRef, useState } from 'react';

import { DARK_CLASS } from './constants';

/**
 * Carries the ambient colour scheme across a portal.
 *
 * The `.dark` class is the whole colour-scheme API (see `constants.ts`): a DS 2.0 component is dark
 * because it SITS somewhere dark, not because it was told to be. Anything that portals to
 * `document.body` breaks that, because it leaves the React tree's position behind — the popup lands
 * as a child of `<body>`, outside whatever ancestor bounded the dark region, and renders light on a
 * dark page.
 *
 * The fix is to look the answer up where the component actually lives. The caller renders the
 * returned `sentinelRef` on a hidden element at its real place in the tree; when `active` flips, this
 * walks up from there for a `.dark` ancestor and hands back the class to put on the portal. The call
 * site still says nothing about theme, which is the point.
 *
 * `useLayoutEffect` rather than `useEffect` deliberately: the portal's first render happens in the
 * same commit as the sentinel's, so resolving the scheme in a passive effect would paint one frame
 * of a light dialog on a dark page before correcting itself.
 *
 * @param active Whether the portalled content is mounted. Re-resolved every time this turns true, so
 *   a dialog that opens after its surroundings changed theme still gets the current answer.
 */
export const useInheritedDarkMode = (active: boolean) => {
  const sentinelRef = useRef<HTMLElement | null>(null);
  const [isDark, setIsDark] = useState(false);

  useLayoutEffect(() => {
    if (!active) {
      return;
    }

    // `closest` walks the DOM tree, not the layout tree, so a `hidden` sentinel answers just as well
    // as a visible one — and costs nothing.
    setIsDark(Boolean(sentinelRef.current?.closest(`.${DARK_CLASS}`)));
  }, [active]);

  return { sentinelRef, inheritedDarkClass: isDark ? DARK_CLASS : undefined };
};
